import { useCallback, useEffect, useMemo } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import * as Tooltip from "@radix-ui/react-tooltip";
import { HelmetProvider } from "react-helmet-async";
import { useShallow } from "zustand/react/shallow";
import { PrivacyScreen } from "@capacitor-community/privacy-screen";

import { useErrorHandler } from "@api/errorHandler/apiErrorHandler";
import { TOAST_CONTAINER_PROPS } from "@configs";
import { SkeletonContainer } from "@components/atoms/loader";
import { axiosClient, queryClient } from "@api/clients.ts";
import { PageMeta } from "@/meta/pageMeta";
import { useAxiosErrorStore } from "@/stores/axiosErrorStore";
import "../i18n/i18n";
import { ThemeProvider } from "@/theme";
import "@/styles/main.css";
import "react-toastify/dist/ReactToastify.css";
import { AppRouter } from "@/routes/appRouter";
import { initSentry } from "@/sentry";
import { useMobile } from "@hooks";

initSentry();

// As early as possible in your JS

export function App() {
  const { i18n } = useTranslation([
    "page",
    "alert",
    "action",
    "common",
    "zod",
    "fieldDescription",
    "field",
    "entity",
    "component",
    "error",
    "pageTitle",
    "sidebar",
    "subtitle",
  ]);

  useEffect(() => {
    PrivacyScreen.disable();
  }, []);

  const { isMobile } = useMobile();

  const setError = useAxiosErrorStore(useShallow((state) => state.setError));

  const errorHandlerFunc = useCallback(
    (message: string, status: number) => {
      setError({ message, status });
    },
    [setError],
  );

  const errorHandler = useErrorHandler(errorHandlerFunc);

  useEffect(() => {
    axiosClient.interceptors.response.use(undefined, errorHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dir = useMemo(() => i18n.dir(i18n.language), [i18n.language]);

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [dir, i18n.language]);

  useEffect(() => {
    document.body.dataset.mobile = isMobile.toString();
  }, [isMobile]);

  return (
    <ThemeProvider>
      <HelmetProvider>
        <PageMeta />
        <Tooltip.Provider delayDuration={0}>
          <QueryClientProvider client={queryClient}>
            <SkeletonContainer>
              <AppRouter />
            </SkeletonContainer>
            <ReactQueryDevtools initialIsOpen={false} />
            <ToastContainer
              {...TOAST_CONTAINER_PROPS}
              rtl={i18n.dir() === "rtl"}
            />
          </QueryClientProvider>
        </Tooltip.Provider>
      </HelmetProvider>
    </ThemeProvider>
  );
}
